// «Открытые заказы»: только позиции кухни. Десктоп — таблица, телефон — карточки (первые 4 и «Показать все»).
// Давние (дольше 2 часов) — время подсвечено.
import React, { useState } from 'react';
import { Box, ButtonBase, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { CARD_LABEL_COLOR, CARD_SURFACE_SX, DIVIDER_COLOR, MUTED_TEXT_COLOR, SECTION_ACCENTS } from '../../../styles/tokens';
import { LONG_OPEN_MINUTES, formatDuration, formatMoney, openedTime, orderTitle, placeColorByName } from '../kitchenFormat';

const LONG_COLOR = '#ff8a5c';
const MOBILE_VISIBLE = 4;
const ACCENT = SECTION_ACCENTS['/kitchen'];

const headCellSx = {
  color: MUTED_TEXT_COLOR,
  fontSize: '13px',
  fontWeight: 500,
  borderBottom: '1px solid rgba(255,255,255,0.08)',
  py: 1,
  px: 1.5,
  whiteSpace: 'nowrap',
};

const cellSx = { color: '#fff', fontSize: '0.9rem', borderBottom: `1px solid ${DIVIDER_COLOR}`, py: 1.25, px: 1.5 };


function Places({ order, places }) {
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '4px 12px' }}>
      {Object.entries(order.places).map(([name, count]) => (
        <Box key={name} component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, fontSize: '13px', color: '#d4d4d8' }}>
          <Box aria-hidden="true" sx={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: placeColorByName(places, name) }} />
          {name} {count}
        </Box>
      ))}
    </Box>
  );
}

const ageColor = (order) => (order.minutes_open >= LONG_OPEN_MINUTES ? LONG_COLOR : undefined);

function OrdersTable({ orders, places }) {
  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Table size="small" sx={{ minWidth: 640 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={headCellSx}>Стол</TableCell>
            <TableCell sx={headCellSx}>Открыт</TableCell>
            <TableCell sx={headCellSx}>Сколько открыт</TableCell>
            <TableCell sx={headCellSx}>Позиции кухни</TableCell>
            <TableCell sx={{ ...headCellSx, textAlign: 'right' }}>Сумма кухни</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {orders.map((order) => (
            <TableRow key={order.id}>
              <TableCell sx={{ ...cellSx, fontWeight: 600, whiteSpace: 'nowrap' }}>
                {orderTitle(order)}{' '}
                {order.section && (
                  <Box component="span" sx={{ fontWeight: 400, color: MUTED_TEXT_COLOR }}>
                    {order.section}
                  </Box>
                )}
              </TableCell>
              <TableCell sx={cellSx}>{openedTime(order.opened)}</TableCell>
              <TableCell sx={{ ...cellSx, color: ageColor(order) || '#fff', whiteSpace: 'nowrap' }}>{formatDuration(order.minutes_open)}</TableCell>
              <TableCell sx={cellSx}>
                <Places order={order} places={places} />
              </TableCell>
              <TableCell sx={{ ...cellSx, textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                {formatMoney(order.sum)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

function OrdersCards({ orders, places }) {
  const [expanded, setExpanded] = useState(false);
  const shown = expanded ? orders : orders.slice(0, MOBILE_VISIBLE);
  return (
    <Box>
      {shown.map((order, index) => (
        <Box key={order.id} sx={{ py: 1.25, borderTop: index === 0 ? 'none' : `1px solid ${DIVIDER_COLOR}`, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 1 }}>
            <Typography sx={{ fontWeight: 600, color: '#fff' }}>
              {orderTitle(order)}
              {order.section ? ` · ${order.section}` : ''}
            </Typography>
            <Typography sx={{ color: '#fff', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{formatMoney(order.sum)}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, fontSize: '12px' }}>
            <Typography component="span" sx={{ fontSize: '12px', color: MUTED_TEXT_COLOR }}>
              открыт {openedTime(order.opened)}
            </Typography>
            <Typography component="span" sx={{ fontSize: '12px', color: ageColor(order) || CARD_LABEL_COLOR }}>
              {formatDuration(order.minutes_open)}
            </Typography>
          </Box>
          <Places order={order} places={places} />
        </Box>
      ))}
      {orders.length > MOBILE_VISIBLE && (
        <ButtonBase
          onClick={() => setExpanded((v) => !v)}
          sx={{ width: '100%', minHeight: 44, borderRadius: '8px', fontWeight: 600, fontSize: '14px', color: ACCENT.tab,
            '&.Mui-focusVisible': { outline: `2px solid ${ACCENT.accent}`, outlineOffset: '2px' } }}
        >
          {expanded ? 'Свернуть' : `Показать все ${orders.length}`}
        </ButtonBase>
      )}
    </Box>
  );
}

export default function OpenOrders({ live, places, desktop }) {
  if (!live?.available || live.orders.length === 0) return null;
  return (
    <Box component="section" aria-labelledby="kitchen-open-title" sx={{ ...CARD_SURFACE_SX, width: '100%', boxSizing: 'border-box', p: { xs: 2, md: 3 }, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', justifyContent: 'space-between', gap: 1 }}>
        <Typography id="kitchen-open-title" component="h2" sx={{ fontSize: { xs: '16px', md: '18px' }, fontWeight: 600, color: '#fff' }}>
          Открытые заказы
        </Typography>
        <Typography sx={{ fontSize: '13px', color: MUTED_TEXT_COLOR }}>Только позиции кухни</Typography>
      </Box>
      {desktop ? <OrdersTable orders={live.orders} places={places} /> : <OrdersCards orders={live.orders} places={places} />}
    </Box>
  );
}
